import type { ILogger } from '@microsoft/signalr';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { from, merge, Observable, of } from 'rxjs';
import { catchError, filter, map, share, tap } from 'rxjs/operators';
import type { IFrontLogger } from 'utils/loggers/FrontLogger';
import { getFrontLogger } from 'utils/loggers/FrontLogger';
import { PriceStreamingEvent } from 'utils/streaming/quotePriceEvent';
import { getSignalRConnectionId } from 'utils/streaming/signalr/connection';
import { SmartQuoteEvent } from 'utils/streaming/smart-quote';

import { sgwtConnect } from '../../../api/connect';
import {
  type BackTestEvent,
  type ConnectionEvent,
  DocumentationEvent,
  isConnectionEvent,
  MailStatusEvent,
  type PriceImprovementEvent,
  type SecondaryMarketEvent,
  type SecondaryMarketPriceEvent,
  type SessionSynchronizationEvent,
  type Streaming,
} from '../model';

interface MessageReceived {
  type: string;
  data: any;
  dataType: string;
  requestId?: string;
}

export interface SignalRStreams<T> extends Omit<Streaming, 'lightEvent$' | 'connectionEvent$'> {
  data$: Observable<T>;
  metadata$: Observable<ConnectionEvent>;
}

interface ISmartLogger extends ILogger {
  isRetrying: boolean;
  elapsedMilliseconds: number;
  elapsedMillisecondsThreshold: number;

  elapsedMillisecondsThresholdStep: number;
  retryCount: number;
}

function remapToInfoLevelIfNeeded(logger: IFrontLogger, msg: string): boolean {
  const matchers: RegExp[] = [
    /Connection disconnected with error 'Error: WebSocket closed with status code: 1006/gi,
    /Connection disconnected with error 'Error: No Connection with that ID: Status code '404'/gi,
    /Connection disconnected with error 'Error: : Status code '401'/gi,
    /Connection disconnected with error 'Error: Server timeout elapsed without receiving a message from the server.'/gi,
    /Connection disconnected with error 'Error: Server returned an error on close: Connection closed with an error.'/gi,
  ];

  if (matchers.some(matcher => msg.match(matcher))) {
    logger.info('SIGNAL-R ' + msg);
    return true;
  }

  return false;
}

function resetSmartLogAdapter(logAdapter: ISmartLogger) {
  logAdapter.isRetrying = false;
  logAdapter.elapsedMilliseconds = 0;
  logAdapter.retryCount = 0;
  logAdapter.elapsedMillisecondsThresholdStep = 60000;
  logAdapter.elapsedMillisecondsThreshold = logAdapter.elapsedMillisecondsThresholdStep;
}

const logAdapter: ISmartLogger = {
  isRetrying: false,
  elapsedMilliseconds: 0,
  retryCount: 0,
  elapsedMillisecondsThresholdStep: 60000,
  elapsedMillisecondsThreshold: 60000,
  log: (level, msg) => {
    const logger = getFrontLogger();
    switch (level) {
      case LogLevel.Critical:
      case LogLevel.Error:
        if (logAdapter.isRetrying && logAdapter.elapsedMilliseconds < logAdapter.elapsedMillisecondsThreshold) {
          break;
        }

        if (logAdapter.isRetrying && logAdapter.elapsedMilliseconds > logAdapter.elapsedMillisecondsThreshold) {
          logAdapter.elapsedMillisecondsThreshold += logAdapter.elapsedMillisecondsThresholdStep;
          logger.info(
            `SIGNAL-R Cannot reconnect after {retryCount} attempts in {elapsedMilliseconds} ms`,
            logAdapter.retryCount,
            logAdapter.elapsedMilliseconds,
          );
          break;
        }

        if (remapToInfoLevelIfNeeded(logger, msg)) {
          break;
        }

        logger.error('SIGNAL-R ' + msg);
        break;

      case LogLevel.Warning:
      case LogLevel.Information:
        logger.info('SIGNAL-R ' + msg);
        break;
      default:
        break;
    }
  },
};

export function connect<T>(hubUrl: string, sgmeConfiguration: SgmeConfiguration): SignalRStreams<T> {
  resetSmartLogAdapter(logAdapter);

  let builder = new HubConnectionBuilder().withUrl(hubUrl, {
    accessTokenFactory: () => `${(sgwtConnect.getAuthorizationHeader() || '').replace('Bearer ', '')}`,
  });

  if (sgmeConfiguration.signalRHubProtocol === undefined || sgmeConfiguration.signalRHubProtocol === 'messagePack') {
    builder = builder.withHubProtocol(new MessagePackHubProtocol());
  }

  const connection = builder
    .configureLogging(logAdapter)
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: ({ elapsedMilliseconds, previousRetryCount }) => {
        logAdapter.isRetrying = true;
        logAdapter.elapsedMilliseconds = elapsedMilliseconds;
        logAdapter.retryCount = previousRetryCount + 1;
        return Math.min(elapsedMilliseconds * 2, 20000);
      },
    })
    .build();

  getFrontLogger().info('SIGNAL-R is ENABLED on hub {hubUrl}', hubUrl);
  connection.onreconnected(_connectionId => {
    resetSmartLogAdapter(logAdapter);
  });
  connection.onreconnecting(_error => {
    resetSmartLogAdapter(logAdapter);
    logAdapter.isRetrying = true;
  });
  connection.onclose(error => {
    resetSmartLogAdapter(logAdapter);
    if (error?.message) {
      const logger = getFrontLogger();
      logger.error('SIGNAL-R ' + error.message);
    }
  });

  const msg$: Observable<MessageReceived> = new Observable(observer => {
    connection.on('ReceiveMessage', (messageType, rawData, dataType, requestId) => {
      observer.next(<MessageReceived>{
        type: messageType,
        data: rawData,
        dataType,
        requestId,
      });
    });
  });

  const secondaryMarketMessage$: Observable<SecondaryMarketEvent> = new Observable(observer => {
    connection.on('SecondaryMarket', rawData => {
      observer.next(<SecondaryMarketEvent>rawData);
    });
  });

  const quotePriceMessage$: Observable<PriceStreamingEvent> = new Observable(observer => {
    connection.on('QuotePrice', rawData => {
      observer.next(rawData as PriceStreamingEvent);
    });
  });

  const smartQuoteMessage$: Observable<SmartQuoteEvent> = new Observable(observer => {
    connection.on('SmartQuote', rawData => {
      observer.next(rawData as SmartQuoteEvent);
    });
  });

  const documentationMessage$: Observable<DocumentationEvent> = new Observable(observer => {
    connection.on('Documentation', rawData => {
      observer.next(rawData as DocumentationEvent);
    });
  });

  const secondaryMarketPriceMessage$: Observable<SecondaryMarketPriceEvent> = new Observable(observer => {
    connection.on('SecondaryPrice', rawData => {
      observer.next(<SecondaryMarketPriceEvent>rawData);
    });
  });

  const sessionSynchronizationMessage$: Observable<SessionSynchronizationEvent> = new Observable(observer => {
    connection.on('SynchronizeSession', rawData => {
      observer.next(<SessionSynchronizationEvent>rawData);
    });
  });

  const priceImprovementMessage$: Observable<PriceImprovementEvent> = new Observable(observer => {
    connection.on('HandlePriceImprovement', rawData => {
      const event = <PriceImprovementEvent>rawData;
      observer.next(event);
    });
  });

  const processBackTestMessage$: Observable<BackTestEvent> = new Observable(observer => {
    connection.on('ProcessBackTest', rawData => {
      observer.next(rawData);
    });
  });

  const mailStatus$: Observable<MailStatusEvent> = new Observable(observer => {
    connection.on('MailStatus', rawData => {
      observer.next(rawData);
    });
  });

  const sessionSynchronizationFilteredMessage$: Observable<SessionSynchronizationEvent> =
    sessionSynchronizationMessage$.pipe(
      filter(sessionEvent => sessionEvent.signalrConnectionId !== getSignalRConnectionId()),
    );

  const disconnected$: Observable<ConnectionEvent> = new Observable(observer => {
    connection.onclose(error => {
      observer.next(<ConnectionEvent>{ event: 'DISCONNECTED', error });
    });
  });

  const reconnecting$: Observable<ConnectionEvent> = new Observable(observer => {
    connection.onreconnecting(error => {
      observer.next(<ConnectionEvent>{ event: 'RECONNECTING', error });
    });
  });

  const reconnected$: Observable<ConnectionEvent> = new Observable(observer => {
    connection.onreconnected(connectionId => {
      observer.next(<ConnectionEvent>{ event: 'RECONNECTED', connectionId });
    });
  });

  const data$: Observable<T> = msg$.pipe(
    filter(({ type }) => type === 'DATA'),
    map(({ data }) => data),
  );

  const connecting$: Observable<ConnectionEvent> = from(connection.start()).pipe(
    map(
      () =>
        ({
          event: 'CONNECTION_SUCCEED',
          connectionId: connection.connectionId,
        }) as ConnectionEvent,
    ),
    catchError((error: Error) =>
      of({
        event: 'CONNECTION_FAILED',
        error,
      } as ConnectionEvent),
    ),
  );
  const metadata$ = merge(disconnected$, reconnecting$, reconnected$, connecting$);

  metadata$
    .pipe(
      tap(evt => {
        const logger = getFrontLogger();
        if (isConnectionEvent(evt)) {
          logger.info('SIGNAL-R connection message {@signalrEvent}', evt);
        } else {
          logger.error('SIGNAL-R invalid event {@invalidSignalRConnectionEvent}', evt);
        }
      }),
    )
    .subscribe(evt => {
      switch (evt.event) {
        case 'CONNECTION_SUCCEED':
        case 'RECONNECTED':
          window.connectionId = evt.connectionId;
          break;
        case 'DISCONNECTED':
        case 'CONNECTION_FAILED':
          window.connectionId = undefined;
          break;
      }
    });

  return {
    connection,
    data$: data$.pipe(share()),
    sessionSynchronizationMessage$: sessionSynchronizationFilteredMessage$.pipe(share()),
    priceImprovementMessage$: priceImprovementMessage$.pipe(share()),
    metadata$: metadata$.pipe(share()),
    secondaryMarketMessage$: secondaryMarketMessage$.pipe(share()),
    secondaryMarketPriceMessage$: secondaryMarketPriceMessage$.pipe(share()),
    backTestMessage$: processBackTestMessage$.pipe(share()),
    priceStreamingMessage$: quotePriceMessage$.pipe(share()),
    documentationMessage$: documentationMessage$.pipe(share()),
    mailStatusMessage$: mailStatus$.pipe(share()),
    smartQuoteMessage$: smartQuoteMessage$.pipe(share()),
  };
}
